// Contract for the CRM email log (CS-215), backed by the `EmailMessages` transactional outbox.
// Mirrors CarpSpecialist.API/Models/EmailLogDtos.cs. All enums are serialized by the API as their
// .NET member NAME (a global JsonStringEnumConverter is registered), so every enum below is a string
// union — never a number. The API also accepts those same names in the query string.
//
// All `DateTime` values arrive as ISO-8601 UTC strings (the AppDbContext value converter stamps
// DateTimeKind.Utc, so the JSON carries a trailing `Z`); keep them as `string` here and format in the
// view layer.

/**
 * Which lifecycle event produced the email. Replaces the legacy numeric `log_type`.
 * The backend list is owned by the senders (CS-216) and may grow; unknown values must degrade
 * gracefully in the UI rather than throw.
 */
export const EmailLogType = {
  BookingRequest: 'BookingRequest',
  BookingConfirmation: 'BookingConfirmation',
  BookingCancellation: 'BookingCancellation',
  BookingVoucher: 'BookingVoucher',
  PaymentInvoice: 'PaymentInvoice',
  PaymentConfirmation: 'PaymentConfirmation',
  PaymentReminder: 'PaymentReminder',
  /** Internal notice to the office that a deposit or remaining balance did not come in. */
  PaymentReminderInternal: 'PaymentReminderInternal',
  ReviewRequest: 'ReviewRequest',
  ServiceOrder: 'ServiceOrder',
  LoginCode: 'LoginCode',
  JoinUserGroup: 'JoinUserGroup',
  ContactAutoReply: 'ContactAutoReply',
  ContactInternal: 'ContactInternal',
  MediaSubmitted: 'MediaSubmitted',
  ExactNotification: 'ExactNotification',
  /** Historical email imported from the legacy Mandrill delivery log. */
  Legacy: 'Legacy',
} as const;
export type EmailLogType = (typeof EmailLogType)[keyof typeof EmailLogType];

/** Declaration order of the backend enum — use for filter dropdowns. */
export const EMAIL_LOG_TYPES: readonly EmailLogType[] = [
  EmailLogType.BookingRequest,
  EmailLogType.BookingConfirmation,
  EmailLogType.BookingCancellation,
  EmailLogType.BookingVoucher,
  EmailLogType.PaymentInvoice,
  EmailLogType.PaymentConfirmation,
  EmailLogType.PaymentReminder,
  EmailLogType.PaymentReminderInternal,
  EmailLogType.ReviewRequest,
  EmailLogType.ServiceOrder,
  EmailLogType.LoginCode,
  EmailLogType.JoinUserGroup,
  EmailLogType.ContactAutoReply,
  EmailLogType.ContactInternal,
  EmailLogType.MediaSubmitted,
  EmailLogType.ExactNotification,
  EmailLogType.Legacy,
];

/**
 * Outbox pipeline state: `Pending` → `Sending` → `Accepted` / `Failed`, plus `Cancelled`.
 * This is our side of the story (did we hand the email to Lettermint?) — what happened afterwards
 * lives in {@link EmailDeliveryStatus}. The two are different axes; never merge them into one enum.
 */
export const EmailMessageStatus = {
  Pending: 'Pending',
  Sending: 'Sending',
  Accepted: 'Accepted',
  Failed: 'Failed',
  Cancelled: 'Cancelled',
} as const;
export type EmailMessageStatus = (typeof EmailMessageStatus)[keyof typeof EmailMessageStatus];

export const EMAIL_MESSAGE_STATUSES: readonly EmailMessageStatus[] = [
  EmailMessageStatus.Pending,
  EmailMessageStatus.Sending,
  EmailMessageStatus.Accepted,
  EmailMessageStatus.Failed,
  EmailMessageStatus.Cancelled,
];

/**
 * Latest post-handoff delivery outcome reported by Lettermint's webhooks. Null until the first
 * webhook arrives — i.e. null does NOT mean "not delivered", it means "nothing reported yet".
 * `HardBounced` / `SpamComplaint` / `Suppressed` are terminal server-side.
 */
export const EmailDeliveryStatus = {
  Sent: 'Sent',
  Delivered: 'Delivered',
  SoftBounced: 'SoftBounced',
  HardBounced: 'HardBounced',
  SpamComplaint: 'SpamComplaint',
  Suppressed: 'Suppressed',
  PolicyRejected: 'PolicyRejected',
  Failed: 'Failed',
  Unsubscribed: 'Unsubscribed',
} as const;
export type EmailDeliveryStatus = (typeof EmailDeliveryStatus)[keyof typeof EmailDeliveryStatus];

export const EMAIL_DELIVERY_STATUSES: readonly EmailDeliveryStatus[] = [
  EmailDeliveryStatus.Sent,
  EmailDeliveryStatus.Delivered,
  EmailDeliveryStatus.SoftBounced,
  EmailDeliveryStatus.HardBounced,
  EmailDeliveryStatus.SpamComplaint,
  EmailDeliveryStatus.Suppressed,
  EmailDeliveryStatus.PolicyRejected,
  EmailDeliveryStatus.Failed,
  EmailDeliveryStatus.Unsubscribed,
];

/**
 * Lettermint `message.*` webhook event names as received in the delivery timeline. Superset of
 * {@link EmailDeliveryStatus}: `Opened` and `Clicked` are logged as events but never promoted to a
 * delivery status.
 */
export const EmailMessageEventType = {
  Sent: 'Sent',
  Delivered: 'Delivered',
  SoftBounced: 'SoftBounced',
  HardBounced: 'HardBounced',
  SpamComplaint: 'SpamComplaint',
  Suppressed: 'Suppressed',
  PolicyRejected: 'PolicyRejected',
  Failed: 'Failed',
  Unsubscribed: 'Unsubscribed',
  Opened: 'Opened',
  Clicked: 'Clicked',
} as const;
export type EmailMessageEventType = (typeof EmailMessageEventType)[keyof typeof EmailMessageEventType];

/**
 * One row of the email log grid. Comes from the narrow outbox table only, so there is no body and no
 * attachment info here — inspect the email for those.
 *
 * `customerName` / `reservationNumber` are resolved per page from the legacy tables and are null when
 * the row carries no id or the referenced record is gone (the outbox keeps no FK by design).
 */
export interface EmailLogListItem {
  /** Outbox row id; the route parameter for inspect/resend/attachments. */
  id: number;
  /** API-facing identifier (GUID string). Stable handle, not used in routes. */
  publicId: string;
  subject: string;
  logType: EmailLogType;
  /** Pipeline state — always present. */
  status: EmailMessageStatus;
  /** Delivery outcome — null until a webhook reports one. */
  deliveryStatus: EmailDeliveryStatus | null;
  /** Recipient address snapshot the email was queued for; a resend goes here. Always present. */
  toEmail: string;
  /** Recipient display-name snapshot. */
  toName: string | null;
  customerId: number | null;
  customerName: string | null;
  reservationId: number | null;
  reservationNumber: string | null;
  /** Moment the email was queued (ISO UTC). Default grid sort, descending. */
  createdOn: string;
  /** Moment Lettermint accepted the message — the real "sent on"; null while queued or failed. */
  acceptedAt: string | null;
  /** Dispatch attempts so far; > 1 means the row has been retried. */
  attemptCount: number;
}

/**
 * Keyset-paginated list response. There are no page numbers: pass {@link nextCursor} back as the
 * `cursor` param to get the following page, and stop when it is null. `totalCount` is still supplied
 * (count over the filtered set, ignoring the cursor) so the UI can show "x results", but it cannot be
 * used to jump to an arbitrary page.
 */
export interface EmailLogListResponse {
  items: EmailLogListItem[];
  /** Rows matching the filters, ignoring the cursor. Display only — not a paging index. */
  totalCount: number;
  /** Page size the server actually used (clamped to 1..100). */
  pageSize: number;
  /** Opaque cursor for the next page; null when this was the last page. */
  nextCursor: string | null;
}

/**
 * Query parameters for the list endpoint. Every field except `pageSize` is optional; null/empty values
 * are omitted from the request.
 *
 * Date fields are `yyyy-MM-dd` (or full ISO) strings. The server treats them as half-open ranges and a
 * date-only `*To` value covers that entire day, so no +1 day adjustment is needed client-side.
 */
export interface EmailLogListParams {
  /** Free text over subject + recipient snapshot (name and address). */
  search: string | null;
  /** Subject substring. */
  subject: string | null;
  /** Exact log type — send the enum NAME, e.g. `BookingConfirmation`. */
  logType: EmailLogType | null;
  /** Exact pipeline state. */
  status: EmailMessageStatus | null;
  /** Exact delivery outcome. */
  deliveryStatus: EmailDeliveryStatus | null;
  /**
   * Free text over the recipient snapshot (name + address). The outbox has no customer navigation, so
   * this matches what the email was addressed to, not the customer's current record.
   */
  customer: string | null;
  /** Exact legacy customer id. */
  customerId: number | null;
  /** Reservation-number prefix, resolved to ids server-side. */
  reservation: string | null;
  /** Exact legacy reservation id. */
  reservationId: number | null;
  /** Queued at or after. */
  createdFrom: string | null;
  /** Queued before (whole day inclusive for a date-only value). */
  createdTo: string | null;
  /** Accepted by the provider at or after. */
  acceptedFrom: string | null;
  /** Accepted before (whole day inclusive for a date-only value). */
  acceptedTo: string | null;
  /** null/empty for the first page, otherwise the previous response's `nextCursor`. */
  cursor: string | null;
  /** 1..100; the server clamps out-of-range values. */
  pageSize: number;
}

/** Attachment metadata shown in the inspect view; the bytes come from the download endpoint. */
export interface EmailLogAttachment {
  id: number;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

/** One provider webhook event in the inspect view's delivery timeline, oldest first. */
export interface EmailLogEvent {
  id: number;
  eventType: EmailMessageEventType;
  /** Provider's event timestamp (ISO UTC) — the authoritative ordering. */
  occurredAt: string;
  /** When our webhook endpoint processed it (ISO UTC). */
  receivedAt: string;
  /** Bounce / failure / suppression reason, when the provider supplied one. */
  reason: string | null;
}

/** Full email as returned by `GET /api/crm/email-logs/{id}` — read-only. */
export interface EmailLogDetail {
  id: number;
  publicId: string;
  subject: string;
  /** Stored, already-rendered HTML body. Render inside a sandboxed iframe. */
  body: string;
  /** Optional plain-text alternative stored alongside the HTML body. */
  textBody: string | null;
  /** Caller-supplied template metadata (name + variables) as a raw JSON string, for debugging. */
  templateData: string | null;
  logType: EmailLogType;
  customerId: number | null;
  customerName: string | null;
  reservationId: number | null;
  reservationNumber: string | null;
  /** Queued moment (ISO UTC). */
  createdOn: string;
  /**
   * Recipient snapshot a resend targets — the address the email was originally sent to, NOT the
   * customer's current address.
   */
  recipientEmail: string | null;
  /** Recipient display-name snapshot. */
  toName: string | null;
  status: EmailMessageStatus;
  /** Provider acceptance moment ("sent on"); null while queued or failed. */
  acceptedAt: string | null;
  /** Lettermint message id; the handle for looking the message up provider-side. */
  providerMessageId: string | null;
  deliveryStatus: EmailDeliveryStatus | null;
  /** Event timestamp of the current `deliveryStatus`. */
  deliveryStatusAt: string | null;
  attemptCount: number;
  /** Retry moment while pending, lease expiry while sending. */
  nextAttemptAt: string | null;
  /** Last send or build failure message; the thing to surface when `status` is `Failed`. */
  lastError: string | null;
  /** Idempotency key of the lifecycle email, if it was queued with one. */
  dedupKey: string | null;
  /** Set when this row is itself a resend: the `id` of the message it copies (route-able). */
  resendOfEmailMessageId: number | null;
  attachments: EmailLogAttachment[];
  events: EmailLogEvent[];
}

/**
 * `202 Accepted` payload of a resend: the identifiers of the newly queued copy. The email is delivered
 * later by the background dispatcher, so nothing is sent yet when this resolves.
 */
export interface EmailLogResendResult {
  id: number;
  publicId: string;
}

/**
 * Single state to show in one grid column: the delivery outcome once the provider reported one,
 * otherwise the pipeline state. Coalescing is presentation only — filtering still happens on the two
 * separate params (`status` / `deliveryStatus`).
 */
export type EmailLogState = EmailMessageStatus | EmailDeliveryStatus;

export function emailLogState(row: {
  status: EmailMessageStatus;
  deliveryStatus: EmailDeliveryStatus | null;
}): EmailLogState {
  return row.deliveryStatus ?? row.status;
}

/**
 * Whether a state should read as a problem in the UI (red chip, error styling). `Failed` is a member of
 * both enums, which is intentional: it means "we could not hand it over" or "delivery failed" depending
 * on which axis it came from, and both are bad news.
 */
export function isEmailLogStateProblem(state: EmailLogState): boolean {
  return (
    state === EmailMessageStatus.Failed ||
    state === EmailDeliveryStatus.SoftBounced ||
    state === EmailDeliveryStatus.HardBounced ||
    state === EmailDeliveryStatus.SpamComplaint ||
    state === EmailDeliveryStatus.Suppressed ||
    state === EmailDeliveryStatus.PolicyRejected
  );
}
